import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiServiceError } from "libs/api";
import { MutationOptions, QueryOptions } from "libs/reactQuery";

import {
  requestCreateDocument,
  requestCreateLog,
  requestDeleteDocument,
  requestDeleteLog,
  requestGetContact,
  requestGetDashboardMetrics,
  requestGetDriverPerformance,
  requestGetRevenue,
  requestGetSla,
  requestUpsertSla,
  requestListDocuments,
  requestListLogs,
  requestUpdateDocument,
  requestUpdateLog,
  requestUpsertContact
} from "./companyProfile.requests";
import {
  CompanyCommunicationLog,
  CompanyLegalDocument,
  CompanyOperationalContact,
  CompanyPeriodParams,
  CompanySla,
  CreateCommunicationLogBody,
  CreateLegalDocumentBody,
  DashboardMetricsResponse,
  DriverPerformanceResponse,
  RevenueParams,
  RevenueResponse,
  UpdateLegalDocumentBody,
  UpsertCompanySlaBody,
  UpsertOperationalContactBody
} from "./companyProfile.types";

const KEYS = {
  sla: (id: string) => ["company-profile", id, "sla"] as const,
  contact: (id: string) => ["company-profile", id, "contact"] as const,
  logs: (id: string) => ["company-profile", id, "logs"] as const,
  docs: (id: string) => ["company-profile", id, "docs"] as const,
  driverPerf: (id: string, params: CompanyPeriodParams) =>
    ["company-profile", id, "driver-performance", params] as const,
  dashboardMetrics: (id: string, params: CompanyPeriodParams) =>
    ["company-profile", id, "dashboard-metrics", params] as const,
  revenue: (id: string, params: RevenueParams) =>
    ["company-profile", id, "revenue", params] as const
};

// ──────────────── SLA ────────────────

export const useGetCompanySla = (
  companyId: string,
  options?: QueryOptions<CompanySla | null, ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.sla(companyId),
    queryFn: () => requestGetSla(companyId),
    enabled: !!companyId,
    ...options
  });

  return { sla: data ?? null, ...rest };
};

export const useUpsertCompanySla = (
  companyId: string,
  options?: MutationOptions<CompanySla, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertCompanySlaBody) =>
      requestUpsertSla(companyId, body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.sla(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

// ──────────────── Operational contact ────────────────

export const useGetCompanyContact = (
  companyId: string,
  options?: QueryOptions<
    CompanyOperationalContact | null,
    ApiServiceError<ApiError>
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.contact(companyId),
    queryFn: () => requestGetContact(companyId),
    enabled: !!companyId,
    ...options
  });

  return { contact: data ?? null, ...rest };
};

export const useUpsertCompanyContact = (
  companyId: string,
  options?: MutationOptions<
    CompanyOperationalContact,
    ApiServiceError<ApiError>
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertOperationalContactBody) =>
      requestUpsertContact(companyId, body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.contact(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

// ──────────────── Communication logs ────────────────

export const useListCompanyLogs = (
  companyId: string,
  options?: QueryOptions<CompanyCommunicationLog[], ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.logs(companyId),
    queryFn: () => requestListLogs(companyId),
    enabled: !!companyId,
    ...options
  });

  return { logs: data ?? [], ...rest };
};

export const useCreateCompanyLog = (
  companyId: string,
  options?: MutationOptions<CompanyCommunicationLog, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCommunicationLogBody) =>
      requestCreateLog(companyId, body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.logs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useUpdateCompanyLog = (
  companyId: string,
  options?: MutationOptions<CompanyCommunicationLog, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; body: CreateCommunicationLogBody }) =>
      requestUpdateLog(companyId, params.id, params.body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.logs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDeleteCompanyLog = (
  companyId: string,
  options?: MutationOptions<{ ok: true }, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => requestDeleteLog(companyId, id),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.logs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

// ──────────────── Legal documents ────────────────

export const useListCompanyDocs = (
  companyId: string,
  options?: QueryOptions<CompanyLegalDocument[], ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.docs(companyId),
    queryFn: () => requestListDocuments(companyId),
    enabled: !!companyId,
    ...options
  });

  return { docs: data ?? [], ...rest };
};

export const useCreateCompanyDoc = (
  companyId: string,
  options?: MutationOptions<CompanyLegalDocument, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLegalDocumentBody) =>
      requestCreateDocument(companyId, body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.docs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useUpdateCompanyDoc = (
  companyId: string,
  options?: MutationOptions<CompanyLegalDocument, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; body: UpdateLegalDocumentBody }) =>
      requestUpdateDocument(companyId, params.id, params.body),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.docs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDeleteCompanyDoc = (
  companyId: string,
  options?: MutationOptions<{ ok: true }, ApiServiceError<ApiError>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => requestDeleteDocument(companyId, id),
    ...options,
    onSuccess: (data, variables, ctx) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.docs(companyId) });
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

// ──────────────── Driver performance ────────────────

export const useGetDriverPerformance = (
  companyId: string,
  params: CompanyPeriodParams,
  options?: QueryOptions<DriverPerformanceResponse, ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.driverPerf(companyId, params),
    queryFn: () => requestGetDriverPerformance(companyId, params),
    enabled: !!companyId,
    ...options
  });

  return { driverPerformance: data ?? null, ...rest };
};

// ──────────────── Dashboard metrics ────────────────

export const useGetDashboardMetrics = (
  companyId: string,
  params: CompanyPeriodParams,
  options?: QueryOptions<DashboardMetricsResponse, ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.dashboardMetrics(companyId, params),
    queryFn: () => requestGetDashboardMetrics(companyId, params),
    enabled: !!companyId,
    ...options
  });

  return { dashboardMetrics: data ?? null, ...rest };
};

// ──────────────── Revenue chart ────────────────

export const useGetRevenue = (
  companyId: string,
  params: RevenueParams,
  options?: QueryOptions<RevenueResponse, ApiServiceError<ApiError>>
) => {
  const { data, ...rest } = useQuery({
    queryKey: KEYS.revenue(companyId, params),
    queryFn: () => requestGetRevenue(companyId, params),
    enabled: !!companyId,
    ...options
  });

  return { revenue: data ?? null, ...rest };
};
